import React, { useState, useEffect } from 'react';
import { getData, getUserProfile } from '../firebase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';
import { exportToCSV, exportToPDF, fmt } from '../exportUtils';

const BRAND = '#0f6e56';

const BUCKETS = [
  { label: 'Not yet due', color: '#0f6e56', bg: '#e1f5ee', min: null, max: 0 },
  { label: '1 – 30 days', color: '#185fa5', bg: '#e6f1fb', min: 1, max: 30 },
  { label: '31 – 60 days', color: '#854f0b', bg: '#faeeda', min: 31, max: 60 },
  { label: '61 – 90 days', color: '#a32d2d', bg: '#fcebeb', min: 61, max: 90 },
  { label: 'Over 90 days', color: '#993556', bg: '#fbeaf0', min: 91, max: null },
];

function daysOverdue(d) { return d ? Math.floor((new Date() - new Date(d)) / 86400000) : 0; }

export default function Aging({ user }) {
  const [invoices, setInvoices] = useState([]);
  const [business, setBusiness] = useState(null);
  const [tab, setTab] = useState('sales');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [data, prof] = await Promise.all([getData(user.uid, 'invoices'), getUserProfile(user.uid)]);
      setInvoices(data); setBusiness(prof); setLoading(false);
    };
    load();
  }, [user.uid]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading...</div>;

  const unpaid = invoices.filter(i => i.type === tab && i.status === 'unpaid');
  const grandTotal = unpaid.reduce((s, i) => s + i.amount, 0);

  const bucketData = BUCKETS.map(b => {
    const items = unpaid.filter(inv => {
      const d = daysOverdue(inv.dueDate);
      if (b.max === 0) return d <= 0;
      if (b.min !== null && b.max !== null) return d >= b.min && d <= b.max;
      if (b.min !== null && b.max === null) return d >= b.min;
      return false;
    });
    return { ...b, items, total: items.reduce((s, i) => s + i.amount, 0) };
  });

  const chartData = bucketData.map(b => ({ name: b.label, amount: b.total, fill: b.color }));

  const doExportCSV = () => {
    const headers = ['Aging Bucket', tab === 'sales' ? 'Customer' : 'Supplier', 'Invoice No', 'Due Date', 'Days Overdue', 'Amount'];
    const rows = [];
    bucketData.forEach(b => b.items.forEach(inv => {
      rows.push([b.label, tab === 'sales' ? inv.customerName : inv.supplierName, inv.invoiceNo, inv.dueDate || 'No due date', daysOverdue(inv.dueDate), inv.amount]);
    }));
    rows.push(['', '', '', '', 'TOTAL', grandTotal]);
    exportToCSV(`Aging_${tab === 'sales' ? 'Receivables' : 'Payables'}`, headers, rows);
  };

  const doExportPDF = () => {
    let body = `<div class="section-title">${tab === 'sales' ? 'Receivables' : 'Payables'} Aging Summary</div><table><thead><tr><th>Aging Bucket</th><th class="num">Invoices</th><th class="num">Amount</th></tr></thead><tbody>`;
    bucketData.forEach(b => { body += `<tr><td>${b.label}</td><td class="num">${b.items.length}</td><td class="num">${fmt(b.total)}</td></tr>`; });
    body += `<tr class="total"><td>TOTAL OUTSTANDING</td><td class="num">${unpaid.length}</td><td class="num green">${fmt(grandTotal)}</td></tr></tbody></table>`;
    bucketData.filter(b => b.items.length > 0).forEach(b => {
      body += `<div class="section-title">${b.label} — ${fmt(b.total)}</div><table><thead><tr><th>${tab === 'sales' ? 'Customer' : 'Supplier'}</th><th>Invoice</th><th>Due Date</th><th class="num">Days</th><th class="num">Amount</th></tr></thead><tbody>`;
      b.items.forEach(inv => { body += `<tr><td>${tab === 'sales' ? inv.customerName : inv.supplierName}</td><td>${inv.invoiceNo}</td><td>${inv.dueDate || '—'}</td><td class="num">${daysOverdue(inv.dueDate)}</td><td class="num">${fmt(inv.amount)}</td></tr>`; });
      body += `</tbody></table>`;
    });
    exportToPDF(`Aging Report — ${tab === 'sales' ? 'Receivables' : 'Payables'}`, business?.businessName, body);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>Aging Report</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={doExportPDF} style={expBtn('#a32d2d', '#fcebeb')}>📄 Export PDF</button>
          <button onClick={doExportCSV} style={expBtn('#0f6e56', '#e1f5ee')}>📊 Export Excel</button>
        </div>
      </div>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Auto-calculated from unpaid invoices</div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8', overflow: 'hidden', width: 'fit-content' }}>
        {[{ id: 'sales', label: '📥 Receivables' }, { id: 'purchase', label: '📤 Payables' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '11px 24px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: tab === t.id ? 600 : 400, background: tab === t.id ? BRAND : '#fff', color: tab === t.id ? '#fff' : '#666', borderRight: '1px solid #e8e8e8' }}>{t.label}</button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={card}>
          <div style={cardTitle}>Outstanding by Age</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => fmt(v)} />
              <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ ...card, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>Total Outstanding</div>
          <div style={{ fontSize: 38, fontWeight: 700, color: tab === 'sales' ? BRAND : '#a32d2d' }}>{fmt(grandTotal)}</div>
          <div style={{ fontSize: 13, color: '#aaa', marginTop: 4 }}>{unpaid.length} unpaid invoices</div>
          {unpaid.length > 0 && (
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={bucketData.filter(b => b.total > 0)} dataKey="total" nameKey="label" cx="50%" cy="50%" innerRadius={30} outerRadius={55}>
                  {bucketData.filter(b => b.total > 0).map((b, i) => <Cell key={i} fill={b.color} />)}
                </Pie>
                <Tooltip formatter={v => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {unpaid.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: BRAND }}>All clear!</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {bucketData.map(b => (
            <div key={b.label} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
              <div style={{ background: b.bg, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: b.color }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: b.color, flex: 1 }}>{b.label}</span>
                <span style={{ fontSize: 13, color: b.color }}>{b.items.length} invoice{b.items.length !== 1 ? 's' : ''}</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: b.color }}>{fmt(b.total)}</span>
              </div>
              {b.items.map((inv, j) => {
                const party = tab === 'sales' ? inv.customerName : inv.supplierName;
                const days = daysOverdue(inv.dueDate);
                return (
                  <div key={j} style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{party}</div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{inv.invoiceNo}{inv.dueDate ? ` · Due: ${inv.dueDate}` : ' · No due date'}{days > 0 ? ` · ${days} days overdue` : ''}</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: b.color }}>{fmt(inv.amount)}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const card = { background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e8e8e8' };
const cardTitle = { fontSize: 15, fontWeight: 600, color: '#222', marginBottom: 16 };
function expBtn(color, bg) { return { padding: '8px 14px', background: bg, color, border: `1px solid ${color}30`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }; }
